// Third-party
import React from 'react';

// Components
import { Cell } from './Cell';

// Types
import ProposalTableRowData from '@/types/proposal-table-row-data';
import Locale from '@/types/locale';

// Utils
import { LOCALE } from '@/utils/i18n';

interface BodyRowProps {
  proposalTableRowData: ProposalTableRowData;
  rowIndex: number;
  locale?: Locale;
  tableId?: string;
  colspan?: number;
  groupHeaderRowIndex?: number | undefined;
  // Temporary prop to handle accessibility permission
  hasAccessibilityPermission?: boolean;
}

export const BodyRow = ({ proposalTableRowData, rowIndex, tableId, locale, hasAccessibilityPermission, colspan, groupHeaderRowIndex }: BodyRowProps) => (
  <tr key={rowIndex} className={proposalTableRowData.className}>
    {proposalTableRowData.data.map((proposalTableCellData, cellIndex: number) => {
      if (proposalTableCellData === undefined || proposalTableCellData.colSpan === 0) {
        return null;
      }

      const style = {
        backgroundColor: proposalTableCellData.backgroundColor,
      };

      const isRowHeader = hasAccessibilityPermission && proposalTableCellData.cellType.name === 'ProposalFieldLabelCell' && cellIndex === 0;

      const isGroupHeader = hasAccessibilityPermission && proposalTableCellData.className?.includes('proposal-table-group') && cellIndex === 0;

      const isAHeader = isRowHeader || isGroupHeader;

      const Tag = isAHeader ? 'th' : 'td';

      const firstCellIsGroupRowHeader = hasAccessibilityPermission && proposalTableRowData.data[0]?.className?.includes('proposal-table-group');

      const isRowGroupHeaderSibling = firstCellIsGroupRowHeader && cellIndex > 0;

      if (isRowGroupHeaderSibling) {
        return null; // Skip sibling cells of row group headers
      }

      const getIdAttribute = () => {
        if (!hasAccessibilityPermission || !tableId || !isAHeader) return undefined;

        if (isGroupHeader) {
          return `${tableId}-group-header-${rowIndex}`;
        }

        if (isRowHeader) {
          return `${tableId}-row-header-${rowIndex}`;
        }

        return undefined;
      };

      const getHeadersAttribute = () => {
        if (!hasAccessibilityPermission || !tableId) return undefined;

        if (isGroupHeader) return undefined;

        // Row headers only reference their group header (if they're in a group)
        if (isRowHeader) {
          if (groupHeaderRowIndex !== undefined) {
            return `${tableId}-group-header-${groupHeaderRowIndex}`;
          }
          return undefined;
        }

        // Data cells reference the appropriate column header (skip cellIndex 0 since that's the row header)
        let headers = `${tableId}-header-${cellIndex}`;

        // Add row header reference if this row has a row header
        if (proposalTableRowData.data[0]?.cellType.name === 'ProposalFieldLabelCell') {
          headers += ` ${tableId}-row-header-${rowIndex}`;
        }

        // Add group header reference if in a group
        if (groupHeaderRowIndex !== undefined) {
          headers += ` ${tableId}-group-header-${groupHeaderRowIndex}`;
        }

        return headers;
      };

      const getScopeAttribute = () => {
        if (isGroupHeader) return 'rowgroup';
        if (isRowHeader) return 'row';
        return undefined;
      };

      return (
        <Tag
          id={getIdAttribute()}
          headers={getHeadersAttribute()}
          key={getIdAttribute()}
          rowSpan={proposalTableCellData.rowSpan}
          colSpan={isGroupHeader && colspan ? colspan : proposalTableCellData.colSpan}
          className={proposalTableCellData.className}
          style={style}
          scope={getScopeAttribute()}
        >
          <Cell proposalTableCellData={proposalTableCellData} locale={locale || LOCALE.EN_CA} />
        </Tag>
      );
    })}
  </tr>
);
